import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import ReactDOMServer from 'react-dom/server';
import classnames from 'classnames';
import OutsideClickHandlerComponent from "../util/OutsideClickHandlerComponent";
import FormattingMenu from "./FormattingMenu";
import { LinkModal } from "./LinkModal";
import {
  getNodeAndOffset,
  processTextWithEntities,
  parseHtmlToEntities,
  getModifiedEntities,
  findLinkedEntity,
  areObjectsEqual,
  isSelectionContainsFormatting,
  onClearSelection,
  replaceCharsWrappedByEntities,
  fixInnerHtml,
} from "../util/format";
import { TextFormats } from "../util/global-variables";
import { isMobileView, isTouchDevice, isVirtualKeyboardAPI } from "../util/device";

import '../index.css';

const FormattingTextarea = ({
  value,
  onChange,
  className,
  entities,
  placeholder,
  inputRef,
  disabled,
  onKeyDown,
  shouldHandleEnterWithModifiers = false,
  hasPro,
  isTrialSubscription,
  onUpgradeClick,
  currentProject,
  messageInput,
  UpgradeToProModal,
  PROFeatures,
  ProFeaturesModal,
  label,
  labelClassName,
  buttonClassName,
  buttonActiveClassName,
  clearClassName,
  menuMobileBottom,
  menuMobileLeft,
  menuMobileBorder,
  ...rest
}) => {
  const editableMessageRef = useRef(null);
  const timerID = useRef(null);
  const isTyping = useRef(false);
  const isInteractingWithMenu = useRef(false);

  const [formattingMenuVisible, setFormattingMenuVisible] = useState(false);
  const [selection, setSelection] = useState({});
  const [linkSelection, setLinkSelection] = useState({});
  const [linkModalOpened, setLinkModalOpened] = useState(false);
  const [upgradeToProModalOpened, setUpgradeToProModalOpened] = useState(false);
  const [formattedHTML, setFormattedHTML] = useState();
  const [clearAllBtnVisible, setClearAllBtnVisible] = useState(false);
  const [proFeaturesModalOpened, setProFeaturesModalOpened] = useState(false);
  // const [needRerender, setNeedRerender] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.visualViewport?.height || window.innerHeight);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (inputRef) {
      inputRef(editableMessageRef.current);
    }
  }, [editableMessageRef]);

  useEffect(() => {
    const isFormattingAppliedInSelection = isSelectionContainsFormatting(selection, entities);
    setClearAllBtnVisible(isFormattingAppliedInSelection);
  }, [selection, entities]);

  const resetState = useCallback(() => {
    setFormattingMenuVisible(false);
    setSelection({});
    setLinkSelection({});
  }, []);

  const handleCompositionStart = () => {
    setIsComposing(true);
  }

  const handleCompositionEnd = () => {
    setIsComposing(false);
  }

  useLayoutEffect(() => {
    if (isTyping.current || isInteractingWithMenu.current) return; // skip if the user is typing
    if (!formattingMenuVisible || !selection.length || !value.length) return; // ensure that selection is only modified when formatting menu is visible

    try {
      if (formattingMenuVisible && selection.length && value.length) {
        const range = document.createRange();
        const { node: startNode, offset: startOffset } = getNodeAndOffset(editableMessageRef.current, selection.offset);
        const { node: endNode, offset: endOffset } = getNodeAndOffset(editableMessageRef.current, selection.offset + selection.length);

        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);

        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (!value?.length) {
        resetState();
      }
    } catch (error) {
      console.error("error: ", error)
    }
  }, [formattingMenuVisible, value, entities, selection.length])

  const getSelection = useCallback(() => {
    const sel = window.getSelection();

    try {
      if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).toString().trim() !== "") {
        const range = sel.getRangeAt(0);
        const selectedText = range.toString();
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editableMessageRef.current);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        const start = preCaretRange.toString().length;
        if (selectedText.length > 0) {
          const containerRect = editableMessageRef?.current?.getBoundingClientRect();
          const selectedRect = range.getBoundingClientRect();
          let relativeX = selectedRect.left;
          let relativeY = selectedRect.top;
          const menuWidth = 366;
          if (relativeX + menuWidth > containerRect.right) {
            relativeX = containerRect.right - menuWidth;
          }

          setFormattingMenuVisible(true);
          setSelection({
            text: selectedText,
            offset: start,
            length: selectedText.length,
            position: { x: relativeX < 0 ? 0 : relativeX, y: relativeY },
          });
        }
      } else if (!sel?.getRangeAt(0).toString().trim().length) {
        resetState();
      }
    } catch (error) {
      console.error("Error in getSelection: ", error)
    }
  }, [value]);

  useEffect(() => {
    processText(value, entities)
  }, []);

  useEffect(() => {
    if (isComposing) return;

    const innerText = editableMessageRef?.current?.innerText?.replace(/\n/g, "");
    const valueText = value?.replace(/\n/g, "");

    if ((value !== "") && (innerText === valueText)) return;

    processText(value, entities);

    if (editableMessageRef.current && (value === "")) {
      editableMessageRef.current.innerText = "";
    }
  }, [value, isComposing, entities]);

  const processText = useCallback((text, formatting) => {
    const newFormattedHTML = processTextWithEntities(text, formatting);
    setFormattedHTML(newFormattedHTML);
  }, []);

  const handleCloseMenu = useCallback(() => {
    if (formattingMenuVisible) {
      setFormattingMenuVisible(false);
    } else {
      setSelection({})
    }
  }, [formattingMenuVisible]);

  const formatLink = useCallback(
    (text, url) => {
      const beforeSelection = value.slice(0, linkSelection.offset);
      const afterSelection = value.slice(linkSelection.offset + linkSelection.length);
      const selectedText = value.slice(linkSelection.offset, linkSelection.offset + linkSelection.length);
      const newValue = beforeSelection + text + afterSelection;

      let updatedEntities = entities.map((entity) => ({
        ...entity,
        offset: entity.offset > linkSelection.offset
          ? entity.offset + (text.length - selectedText.length)
          : entity.offset,
      }));

      if (linkSelection.url) {
        const existingEntity = {
          type: TextFormats.TEXT_LINK,
          offset: linkSelection.offset,
          length: linkSelection.length,
          url: linkSelection.url
        }
        updatedEntities = updatedEntities.filter(
          (entity) => !areObjectsEqual(entity, existingEntity)
        );
      }
      const newEntity = {
        type: TextFormats.TEXT_LINK,
        offset: linkSelection.offset,
        length: text.length,
        url,
      }
      onModifyEntities(newValue, newEntity, updatedEntities);
      setLinkSelection({});
    },
    [value, linkSelection, onChange, entities]
  );

  const onModifyEntities = useCallback((textValue, newEntity, entitiesArr) => {
    const modifiedEntities = getModifiedEntities(newEntity, entitiesArr);
    onChange(textValue, modifiedEntities);
    processText(textValue, modifiedEntities);
  },[processText, onChange]);

  const onTextFormatting = useCallback((type) => {
    if (!hasPro && UpgradeToProModal && ProFeaturesModal) {
      if(isMobileView()) {
        setUpgradeToProModalOpened(true);
      } else {
        setProFeaturesModalOpened(true);
      }
      handleCloseMenu();
      return;
    }
    switch (type) {
      case TextFormats.CLEAR:
        const newEntities = onClearSelection(selection, entities, selection.length === value.length);
        onChange(value, newEntities);
        processText(value, newEntities);
        return;
      case TextFormats.TEXT_LINK: {
        const existingLink = findLinkedEntity(selection, entities);
        if (existingLink) {
          setLinkSelection({
            text: value.substring(existingLink.offset, existingLink.offset + existingLink.length),
            offset: existingLink.offset,
            length: existingLink.length,
            url: existingLink.url
          })
        } else {
          setLinkSelection(selection);
        }
        setLinkModalOpened(true);
        handleCloseMenu();
        return;
      }

      default: {
        let newEntity = {
          type,
          offset: selection.offset,
          length: selection.length,
        };
        onModifyEntities(value, newEntity, entities);
        break;
      }
    }
  }, [hasPro, isMobileView, setUpgradeToProModalOpened, setProFeaturesModalOpened, handleCloseMenu, selection, entities, value, onChange, processText, onModifyEntities]);

  function moveCursorInInput(direction) {
    try {
      const sel = window.getSelection();
      const range  = sel.getRangeAt(0);
      const newRange = document.createRange();
      newRange.setStart(range.startContainer, range.endOffset + direction);
      newRange.setEnd(range.startContainer, range.endOffset + direction);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } catch (e) {
      console.error("Error in moveCursorInInput: ", e)
    }
  }

  const handleInputChange = useCallback(
    (event) => {
      isTyping.current = true;
      let newText = event.target.innerText;
      if (!newText.length) {
        onChange(newText, []);
        isTyping.current = false;
        return;
      }
      const sel = window.getSelection();
      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editableMessageRef.current);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      let caretOffset = preCaretRange.toString().length;

      if (caretOffset > newText.length) {
        caretOffset = newText.length;
      }
      if (timerID.current !== null) {
        clearTimeout(timerID.current);
        timerID.current = null;
      }
      const innerHtml = editableMessageRef.current.innerHTML;
      const { html: fixedHtml, quoteTore } = fixInnerHtml(innerHtml);
      const { entities: updatedEntities, text: fixedInnerText } = parseHtmlToEntities(fixedHtml);

      if (quoteTore) {
        timerID.current = setTimeout(() => {
          onChange(fixedInnerText, updatedEntities);
          processText(fixedInnerText, updatedEntities);
          try {
            const updatedRange = document.createRange();
            const { node, offset } = getNodeAndOffset(editableMessageRef.current, caretOffset);
            updatedRange.setStart(node, offset);
            updatedRange.collapse(true);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(updatedRange);
            if (quoteTore) {
              moveCursorInInput(-1);
            }
          } catch (error) {
            // TODO: This is temporary fix to avoid crashes. Better solution is needed
            const currentNode = editableMessageRef.current;
            if (currentNode && currentNode.childNodes?.length) {
              const lastNode = currentNode.childNodes[currentNode.childNodes.length - 1];
              if (!lastNode) return;
              const updatedRange = document.createRange();
              updatedRange.selectNode(lastNode);
              updatedRange.setStart(lastNode, 0);
              updatedRange.setEnd(lastNode, 0);
              sel.removeAllRanges();
              sel.addRange(updatedRange);
            }
          }
          if (!isInteractingWithMenu.current) {
            setSelection({});
          }
          isTyping.current = false;
        }, 100);
      } else {
        onChange(fixedInnerText, updatedEntities);
        if (!isInteractingWithMenu.current) {
          setSelection({});
        }
        isTyping.current = false;
      }
    },
    [onChange, processText, editableMessageRef, timerID?.current]
  );

  function getCursorPosition(editableDiv) {
    var range = window.getSelection().getRangeAt(0);
    var preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(editableDiv);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    return preSelectionRange.toString().length;
  }

  function insertNewline() {
    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    const rangeText = range.commonAncestorContainer.wholeText;

    const wholeText = replaceCharsWrappedByEntities(editableMessageRef.current.innerText, entities);

    const cursorOffset = getCursorPosition(editableMessageRef.current);

    const cursorCharacter = wholeText[cursorOffset];
    const rangeLastCharacter = rangeText ? rangeText[rangeText.length - 1] : undefined;
    const isCursorAtQuoteBorder = entities.find(entity => (
      entity.type === TextFormats.BLOCKQUOTE
      && (entity.offset === cursorOffset || entity.offset + entity.length === cursorOffset)
    ));
    const isCursorAtQuoteStart = entities.find(entity => (
      entity.type === TextFormats.BLOCKQUOTE && (entity.offset === cursorOffset)
    ));
    const entitiesOffset = entities
      .map(entity =>
        entity.type !== TextFormats.BLOCKQUOTE
          ? 0
          : (entity.offset === 0 || entity.offset + entity.length === wholeText.length)
            ? 1
            : 2
      )
      .reduce((acc, val) => acc + val, 0);

    const isCursorAtEndText = (cursorOffset + entitiesOffset) === wholeText.length;
    const isCursorAtEndRange = rangeText ? rangeText.length === range.endOffset : true;
    const isCursorNotAtNL = cursorCharacter !== "\n";
    const isLastCharacterNotNL = rangeLastCharacter !== "\n";

    if (
      (isCursorAtEndText && wholeText[wholeText.length - 1] !== "\n")
      || (isCursorAtQuoteBorder && isCursorAtEndRange && isCursorNotAtNL && isLastCharacterNotNL && range.startOffset !== 0)
    ) {
      // to avoid double enter click for newLine
      document.execCommand("insertHTML", false, "\n\n");
    } else {
      document.execCommand("insertHTML", false, "\n");
    }

    const isParentInRangeQuote = range.commonAncestorContainer.parentElement.nodeName === "BLOCKQUOTE";
    if (!isParentInRangeQuote && isCursorAtQuoteStart && isCursorAtEndRange && isCursorNotAtNL && isLastCharacterNotNL) {
      setTimeout(() => {
        moveCursorInInput(-1);
      }, 100);
    }
  }

  const handleKeyDown = (e) => {
    const { key, ctrlKey, metaKey, shiftKey, which } = e;
    if (onKeyDown) {
      onKeyDown(e);
    }
    if (key === "Enter" && !shiftKey && !shouldHandleEnterWithModifiers) {
      // e.stopPropagation();
      e.preventDefault();
      insertNewline();
    }
    if (key === "Backspace") {
      // setNeedRerender(true);
    }
    if (ctrlKey || metaKey) {
      if (!shiftKey) {
        switch (which) {
          case 66: // b
          case 73: // i
          case 75: // k
          case 85: // u
            e.preventDefault();
            onTextFormatting(which === 66 ? "bold" : which === 73 ? "italic" : which === 85 ? "underline" : "text_link");
            break;
          default:
            break;
        }
      } else {
        switch (which) {
          case 77: // m
          case 80: // p
          case 88: // x
            e.preventDefault();
            onTextFormatting(which === 88 ? "strikethrough" : which === 77 ? "code" : "spoiler");
            break;
          default:
            break;
        }
      }
    }
  };

  const onUpgrade = useCallback(() => {
    if (PROFeatures && onUpgradeClick) {
      onUpgradeClick(PROFeatures.TEXT_FORMATTING)
    }
  },[]);

  useEffect(() => {
    const handleKeyboardGeometryChange = (event) => {
      const { y, height } = event.target.boundingRect;
      const newViewportHeight = window.innerHeight;

      if (newViewportHeight > viewportHeight) {
        setFormattingMenuVisible(false);
      }
      setViewportHeight(newViewportHeight);
    };

    const handleViewportResize = () => {
      const newHeight = window.visualViewport?.height || window.innerHeight;
      if (newHeight !== viewportHeight) {
        setFormattingMenuVisible(false);
      }
      setViewportHeight(newHeight);
    };

    if (isTouchDevice() && window.visualViewport && isVirtualKeyboardAPI) {
      try {
        navigator.virtualKeyboard.overlaysContent = true;
        navigator.virtualKeyboard.addEventListener("geometrychange", handleKeyboardGeometryChange);
      } catch (error) {
        console.log("Error setting virtualKeyboard.overlaysContent:", error);
      }
    }

    if (isTouchDevice() && window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportResize);
    }

    return () => {
      if (isTouchDevice() && window.visualViewport && isVirtualKeyboardAPI) {
        navigator.virtualKeyboard.removeEventListener("geometrychange", handleKeyboardGeometryChange);
      }
      if (isTouchDevice() && window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleViewportResize);
      }
    };
  }, [viewportHeight, resetState]);

  const innerText = editableMessageRef?.current?.innerText?.replace(/\n/g, "");
  const valueText = value?.replace(/\n/g, "");

  return (
    <div className="rtite-root tw-preflight relative">
      <OutsideClickHandlerComponent
        className="z-50 relative"
        onOutsideClick={handleCloseMenu}
      >
        {formattingMenuVisible ? (
          <FormattingMenu
            showClearAllBtn={clearAllBtnVisible}
            onOptionSelect={onTextFormatting}
            menuPosition={selection.position}
            hasPro={hasPro}
            isTrialSubscription={isTrialSubscription}
            messageInput={messageInput}
            editableMessageRef={editableMessageRef}
            buttonClassName={buttonClassName}
            buttonActiveClassName={buttonActiveClassName}
            clearClassName={clearClassName}
            entities={entities}
            selection={selection}
            menuMobileBottom={menuMobileBottom}
            menuMobileLeft={menuMobileLeft}
            menuMobileBorder={menuMobileBorder}
          />
        ) : <div style={{ display: 'none' }}></div>}
      </OutsideClickHandlerComponent>
      <div
        id="editable-text"
        ref={editableMessageRef}
        className={classnames(
          'peer whitespace-pre-wrap',
          className,
        )}
        contentEditable={!disabled}
        placeholder={placeholder}
        role="textbox"
        type="text"
        aria-label="Editable Text"
        style={{ overflowWrap: "anywhere" }}
        onInput={handleInputChange}
        dangerouslySetInnerHTML={{ __html: ReactDOMServer.renderToStaticMarkup(formattedHTML) }}
        onKeyDown={handleKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onSelect={getSelection}
      />
      {label && (
        <label className={labelClassName}>
          {label}
        </label>
      )}
      <LinkModal
        modalOpen={linkModalOpened}
        textValue={linkSelection.text}
        urlValue={linkSelection.url}
        setModalOpen={setLinkModalOpened}
        formatLink={formatLink}
      />
      {UpgradeToProModal && PROFeatures && (
        <UpgradeToProModal
          modalOpen={upgradeToProModalOpened}
          setModalOpen={setUpgradeToProModalOpened}
          featureId={PROFeatures.TEXT_FORMATTING}
          onUpgrade={onUpgrade}
        />
      )}
      {ProFeaturesModal && PROFeatures && (
        <ProFeaturesModal
          featureId={PROFeatures.TEXT_FORMATTING}
          modalOpen={proFeaturesModalOpened}
          setModalOpen={setProFeaturesModalOpened}
        />
      )}
    </div>
  );
}

export default FormattingTextarea;
